<template>
  <div class="user-form-container">
    <BForm @submit.prevent="handleSubmit">
      <BRow class="mb-3">
        <BCol md="6" class="mb-3">
          <BFormGroup label="Nom" label-for="lastName">
            <template #label>
              <span class="form-label-text">
                Nom<span class="text-danger required-star"> *</span>
              </span>
            </template>
            <BFormInput
              id="lastName"
              v-model="formData.lastName"
              type="text"
              placeholder="Nom"
              required
            />
          </BFormGroup>
        </BCol>
        <BCol md="6" class="mb-3">
          <BFormGroup label="Prénom" label-for="firstName">
            <template #label>
              <span class="form-label-text">
                Prénom<span class="text-danger required-star"> *</span>
              </span>
            </template>
            <BFormInput
              id="firstName"
              v-model="formData.firstName"
              type="text"
              placeholder="Prénom"
              required
            />
          </BFormGroup>
        </BCol>
      </BRow>

      <BRow class="mb-3">
        <BCol md="6" class="mb-3">
          <BFormGroup label="Email" label-for="email">
            <template #label>
              <span class="form-label-text">
                Email<span class="text-danger required-star"> *</span>
              </span>
            </template>
            <BFormInput
              id="email"
              v-model="formData.email"
              type="email"
              placeholder="Email"
              :disabled="mode === 'update'"
              required
            />
          </BFormGroup>
        </BCol>
        <BCol md="6" class="mb-3">
          <BFormGroup label="Téléphone" label-for="phone">
            <template #label>
              <span class="form-label-text">
                Téléphone<span class="text-danger required-star"> *</span>
              </span>
            </template>
            <BFormInput
              id="phone"
              v-model="formData.phone"
              type="tel"
              placeholder="Téléphone"
              required
            />
          </BFormGroup>
        </BCol>
      </BRow>

      <BRow class="mb-3">
        <BCol md="6">
          <BFormGroup label="Code enquêteur" label-for="codeEnqueteur">
            <template #label>
              <span class="form-label-text"> Code enquêteur </span>
            </template>
            <BFormInput
              id="codeEnqueteur"
              v-model="formData.codeEnqueteur"
              type="text"
              placeholder="Code enquêteur"
            />
          </BFormGroup>
        </BCol>
      </BRow>

      <BRow class="mb-3">
        <BCol md="12">
          <BFormGroup label="Role" label-for="role">
            <template #label>
              <span class="form-label-text">
                Role<span class="text-danger required-star"> *</span>
              </span>
            </template>

            <div class="role-options-wrapper">
              <div v-if="loadingRoles" class="text-muted">Loading roles...</div>

              <div v-else-if="rolesError" class="text-danger">
                {{ rolesError }}
              </div>

              <div v-else class="role-options">
                <BFormRadio
                  v-for="role in selectableRoles"
                  :key="role.id"
                  v-model="formData.role_name"
                  name="role"
                  :value="role.name"
                >
                  {{ role.name }}
                </BFormRadio>
              </div>
            </div>
          </BFormGroup>
        </BCol>
      </BRow>

      <div class="button-row">
        <BButton variant="link" class="text-danger" @click="handleCancel">
          × Cancel
        </BButton>
        <BButton variant="primary" type="submit" :disabled="loadingRoles">
          {{ submitButtonText }}
        </BButton>
      </div>
    </BForm>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { roleService } from '@/shared/services/role.service';
import { ROLES } from '@/constants/roles';

export default {
  name: 'UserForm',

  props: {
    title: {
      type: String,
      default: 'Add User',
    },
    submitButtonText: {
      type: String,
      default: 'Add User',
    },
    user: {
      type: Object,
      default: null,
    },
    mode: {
      type: String,
      default: 'create',
      validator: (value) => ['create', 'update'].includes(value),
    },
  },

  data() {
    return {
      formData: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        role_name: '',
      },
      roles: [],
      loadingRoles: false,
      rolesError: null,
    };
  },

  computed: {
    ...mapGetters('auth', ['userRole']),

    /**
     * Les roles qu'on a le droit d'attribuer. Seul un SuperAdmin cree un Admin:
     * un Admin qui pourrait en creer un autre s'accorderait tout seul des pairs.
     * L'API masque deja SuperAdmin (`hidden`), on l'ecarte quand meme ici pour
     * ne pas dependre d'un filtre distant.
     */
    selectableRoles() {
      if (this.userRole === ROLES.SUPER_ADMIN) {
        return this.roles;
      }

      // En modification, le role actuel de la personne reste affiche meme s'il
      // n'est pas attribuable: le retirer reviendrait a le changer en douce au
      // premier enregistrement.
      const currentRole = this.user
        ? this.getRoleNameById(this.user.roleId)
        : null;

      return this.roles.filter(
        (role) =>
          role.name === currentRole ||
          (role.name !== ROLES.ADMIN && role.name !== ROLES.SUPER_ADMIN),
      );
    },
  },

  mounted() {
    this.fetchRoles();
    this.populateFormData();
  },

  watch: {
    user: {
      handler() {
        this.populateFormData();
      },
      deep: true,
    },
  },

  methods: {
    async fetchRoles() {
      this.loadingRoles = true;
      this.rolesError = null;

      try {
        const data = await roleService.getRoles();
        this.roles = data;

        if (this.user) {
          this.formData.role_name = this.getRoleNameById(this.user.roleId);
        }
      } catch (error) {
        console.error('Error fetching roles:', error);
        this.rolesError = 'Failed to load roles. Please try again.';
      } finally {
        this.loadingRoles = false;
      }
    },

    getRoleNameById(roleId) {
      const role = this.roles.find((r) => r.id === roleId);
      return role ? role.name : null;
    },
    populateFormData() {
      if (this.user) {
        this.formData = {
          lastName: this.user.lastName,
          firstName: this.user.firstName,
          email: this.user.email,
          phone: this.user.phone,
          role_name: this.getRoleNameById(this.user.roleId),
        };
        if (this.formData.codeEnqueteur) {
          this.formData['codeEnqueteur'] = this.user.codeEnqueteur;
        }
      }
    },

    handleSubmit() {
      this.$emit('user-submit', { ...this.formData });
    },

    handleCancel() {
      this.$emit('cancel');
      if (this.mode === 'create') {
        this.formData = {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          role_name:
            this.selectableRoles.length > 0 ? this.selectableRoles[0].name : '',
          codeEnqueteur: '',
        };
      } else {
        if (this.user) {
          this.formData = {
            lastName: this.user.lastName,
            firstName: this.user.firstName,
            email: this.user.email,
            phone: this.user.phone,
            role_name: this.getRoleNameById(this.user.roleId),
          };
        }
      }
    },
  },
};
</script>

<style scoped>
.form-label-text {
  font-weight: 400;
  font-size: 0.8rem;
}

.user-form-container {
  background-color: white;
  width: 100%;
  padding: 2rem;
  text-align: left;
}

.page-title {
  font-size: 1rem;
  font-weight: 400;
  color: #333;
  margin-bottom: 1rem;
  border: none;
  font-family: 'Poppins', sans-serif;
}
.separator {
  height: 1px;
  margin-bottom: 2.25rem;
  border: solid 1px;
}

.role-options {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.role-options .custom-control {
  font-size: 1rem;
}

.role-options
  .custom-radio
  .custom-control-input:checked
  ~ .custom-control-label::before {
  background-color: #2563eb;
  border-color: #3b82f6;
}

.button-row {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
}

.button-row .btn-primary {
  background-color: #3b82f6;
  border-color: #3b82f6;
}

.button-row .btn-primary:hover {
  background-color: #2563eb;
  border-color: #2563eb;
}

.button-row .btn-primary:focus,
.button-row .btn-primary:active {
  background-color: #2563eb;
  border-color: #2563eb;
  box-shadow: 0 0 0 0.2rem rgba(59, 130, 246, 0.5);
}

.user-form-container {
  font-family: 'Poppins', sans-serif;
}
.role-options-wrapper {
  margin-top: 0.5rem;
}
</style>
